// Character Registry modal — the per-client curated character list (Run 2,
// Builds 2+3). Opened by clicking the Character column header in the script
// editor; scoped to that script's client.
//
// The registry is a picker, not a source of truth (D-011): renaming here never
// rewrites a script body, and the modal says so plainly on every rename.
//
// §3.1 — a casting note is never a casting. A character with no defaultTalent is
// UNCAST whatever its note says; the note renders as muted italics behind an
// explicit "uncast" marker so nobody can mistake it for a booked person.

import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';

const RENAME_WARNING = 'Renaming affects new script lines only. Lines already written keep the old name.';

export default function CharacterRegistryModal({
  clientId, clientName, onClose,
  canEdit,       // add / paste / rename / archive / restore / delete
  canMapTalent,  // set defaultTalentId (Producer / Power User / Admin)
  onChanged,     // notify the editor so the cell picker refetches
}) {
  const [rows,       setRows]       = useState(null);   // null = loading
  const [talents,    setTalents]    = useState([]);     // active Talent-role members
  const [errMsg,     setErrMsg]     = useState('');
  const [notice,     setNotice]     = useState('');
  const [newName,    setNewName]    = useState('');
  const [pasteOpen,  setPasteOpen]  = useState(false);
  const [pasteText,  setPasteText]  = useState('');
  const [busy,       setBusy]       = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [editName,   setEditName]   = useState('');
  const [showArchived, setShowArchived] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/api/client-characters?clientId=${clientId}`);
      setRows(r.data.characters || []);
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'Could not load the character list.');
      setRows([]);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);
  useEffect(() => {
    if (!canMapTalent) return;
    api.get('/api/team-members')
      .then(r => setTalents((r.data || []).filter(m =>
        m.isActive !== false && m.roles?.some(x => x.role === 'Talent'))))
      .catch(() => {});
  }, [canMapTalent]);

  const changed = () => { onChanged?.(); };

  async function run(fn, okMsg) {
    setBusy(true); setErrMsg(''); setNotice('');
    try { await fn(); if (okMsg) setNotice(okMsg); await load(); changed(); }
    catch (e) { setErrMsg(e.response?.data?.error || 'That did not work.'); }
    finally { setBusy(false); }
  }

  const addSingle = () => {
    const name = newName.trim();
    if (!name) return;
    run(async () => {
      await api.post('/api/client-characters', { clientId, name });
      setNewName('');
    }, `Added “${name}”.`);
  };

  const importPaste = () => run(async () => {
    const r = await api.post('/api/client-characters/import', { clientId, text: pasteText });
    const { addedCount, alreadyPresentCount, rejected = [] } = r.data;
    setNotice(
      `${addedCount} added, ${alreadyPresentCount} already present` +
      (rejected.length ? `, ${rejected.length} rejected (no letters)` : '') + '.');
    setPasteText(''); setPasteOpen(false);
  });

  const saveRename = (row) => {
    const name = editName.trim();
    if (!name || name === row.name) { setEditingId(null); return; }
    run(async () => {
      await api.patch(`/api/client-characters/${row.id}`, { name });
      setEditingId(null);
    }, `Renamed to “${name}”. ${RENAME_WARNING}`);
  };

  const setTalent = (row, talentId) => run(async () => {
    await api.patch(`/api/client-characters/${row.id}`, { defaultTalentId: talentId || null });
  });

  const archive = (row) => run(async () => {
    await api.post(`/api/client-characters/${row.id}/archive`);
  }, `Archived “${row.name}”.`);

  const restore = (row) => run(async () => {
    await api.post(`/api/client-characters/${row.id}/restore`);
  }, `Restored “${row.name}”.`);

  const remove = (row) => run(async () => {
    await api.delete(`/api/client-characters/${row.id}`);
  }, `Deleted “${row.name}”.`);

  const live     = useMemo(() => (rows || []).filter(r => !r.isArchived), [rows]);
  const archived = useMemo(() => (rows || []).filter(r =>  r.isArchived), [rows]);

  // §3.1 — the three talent states, visually distinct. `uncastChip` is the
  // explicit marker; the note is muted italics BEHIND it, never a bare name.
  // DEF-108: the chip is a button — clicking it focuses and opens the row's
  // default-talent picker (the same select). It never removes anything.
  const uncastChip = (
    <button
      type="button"
      data-testid="registry-uncast-chip"
      disabled={!canMapTalent}
      title={canMapTalent ? 'Uncast — click to pick the standing talent for this character' : 'Uncast — a Producer, Power User or Admin maps talent'}
      onClick={e => {
        e.stopPropagation();
        const sel = e.currentTarget.parentElement?.querySelector('select');
        if (sel) { sel.focus(); try { sel.showPicker?.(); } catch { /* needs a user gesture in some browsers — focus is enough */ } }
      }}
      style={{
        padding: '1px 7px', borderRadius: 3, fontSize: 9, fontWeight: 700,
        letterSpacing: '0.05em', textTransform: 'uppercase', fontFamily: 'var(--font)',
        background: 'rgba(140,140,160,0.14)', color: 'var(--tx-muted)',
        border: '1px dashed var(--border-mid)', flexShrink: 0, cursor: canMapTalent ? 'pointer' : 'default',
      }}>uncast</button>
  );

  function talentCell(row) {
    const picker = canMapTalent && (
      <select
        className="form-input"
        style={{ fontSize: 11, padding: '3px 6px', maxWidth: 150 }}
        value={row.defaultTalentId || ''}
        disabled={busy}
        onChange={e => setTalent(row, e.target.value)}
        title="Standing cast for this character — the picker lists Talent-role members only"
      >
        <option value="">No default talent…</option>
        {talents.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        {/* keep an unlisted current value visible rather than silently blanking it */}
        {row.defaultTalentId && !talents.some(t => t.id === row.defaultTalentId) && (
          <option value={row.defaultTalentId}>{row.defaultTalent?.name || 'Unknown member'}</option>
        )}
      </select>
    );
    if (row.defaultTalentId) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {picker || <span style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>{row.defaultTalent?.name}</span>}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
        {picker}
        {uncastChip}
        {row.castingNote && (
          <span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--tx-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={`Writers' note — not a casting: “${row.castingNote}”`}>
            note: {row.castingNote}
          </span>
        )}
      </div>
    );
  }

  function liveRow(row) {
    const isEditing = editingId === row.id;
    return (
      <div key={row.id} style={{
        display: 'grid', gridTemplateColumns: canEdit ? 'minmax(120px,1fr) minmax(180px,1.4fr) auto' : 'minmax(120px,1fr) minmax(180px,1.4fr)',
        gap: 10, alignItems: 'center', padding: '7px 10px',
        background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      }}>
        {isEditing ? (
          <input
            className="form-input" autoFocus value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveRename(row); if (e.key === 'Escape') setEditingId(null); }}
            style={{ fontSize: 12, padding: '4px 8px' }}
          />
        ) : (
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--tx-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.name}
          </div>
        )}
        {talentCell(row)}
        {canEdit && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {isEditing ? (
              <>
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} disabled={busy} onClick={() => saveRename(row)}>Save</button>
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setEditingId(null)}>Cancel</button>
              </>
            ) : (
              <>
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} disabled={busy}
                  title={RENAME_WARNING}
                  onClick={() => { setEditingId(row.id); setEditName(row.name); setNotice(RENAME_WARNING); }}>✎</button>
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} disabled={busy}
                  title="Archive — reversible, keeps history, frees the name"
                  onClick={() => archive(row)}>Archive</button>
                <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px', color: 'var(--s-incomplete-t)' }} disabled={busy}
                  title="Delete — only possible when unmapped and used in no script"
                  onClick={() => remove(row)}>✕</button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 620, maxHeight: '84vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-title">Characters — {clientName}</div>
            <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 2 }}>
              The curated list the Character cell suggests from. Picking one still writes the plain name into the script.
            </div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }} data-testid="character-registry-modal">
          {errMsg && <div style={{ fontSize: 12, color: 'var(--s-incomplete-t)', marginBottom: 10 }}>{errMsg}</div>}
          {notice && <div style={{ fontSize: 12, color: 'var(--violet)', marginBottom: 10 }} data-testid="registry-notice">{notice}</div>}

          {canEdit && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input" placeholder="Add a character…" value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSingle(); } }}
                  style={{ flex: 1, fontSize: 12, padding: '6px 10px' }}
                  data-testid="registry-add-input"
                />
                <button className="btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} disabled={busy || !newName.trim()} onClick={addSingle}>Add</button>
                <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setPasteOpen(v => !v)} data-testid="registry-paste-toggle">
                  {pasteOpen ? 'Hide paste' : 'Paste a list…'}
                </button>
              </div>
              {pasteOpen && (
                <div style={{ marginTop: 8 }}>
                  <textarea
                    className="form-input" rows={6} placeholder={'One character per line…'}
                    value={pasteText} onChange={e => setPasteText(e.target.value)}
                    style={{ width: '100%', fontSize: 12, padding: '8px 10px', boxSizing: 'border-box', resize: 'vertical' }}
                    data-testid="registry-paste-textarea"
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                    <button className="btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} disabled={busy || !pasteText.trim()} onClick={importPaste} data-testid="registry-paste-import">
                      Import
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {rows === null ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>Loading…</div>
          ) : live.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>No characters yet{canEdit ? ' — add one above or paste the project’s list.' : '.'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="registry-live-list">
              {live.map(liveRow)}
            </div>
          )}

          {archived.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setShowArchived(v => !v)}>
                {showArchived ? '▾' : '▸'} Archived ({archived.length})
              </button>
              {showArchived && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {archived.map(row => (
                    <div key={row.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      padding: '6px 10px', background: 'var(--bg-hover)',
                      border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', opacity: 0.75,
                    }}>
                      <span style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>{row.name}</span>
                      {canEdit && (
                        <button className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} disabled={busy} onClick={() => restore(row)}>Restore</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
